import { inject, Injectable } from '@angular/core';
import { firstValueFrom, map, Observable, take } from 'rxjs';
import { ConfirmActionModalService } from './confirm-action-modal/confirm-action-modal.service';
import { FormEditInlineModel } from '../guards/unsaved-changes.guard';

@Injectable({
  providedIn: 'root',
})
export class FormEditModeService {
  private readonly confirmActionModalService = inject(ConfirmActionModalService);

  private activeComponents = new Set<FormEditInlineModel>();

  public get hasUnsavedChanges(): boolean {
    return Array.from(this.activeComponents).some((component) => component.hasUnsavedChanges);
  }

  public showConfirmEditModeCancelModal(): Observable<boolean> {
    return this.confirmActionModalService
      .showModal({
        title: 'BEARBEITUNGSMODUS_BEENDEN',
        infoText: 'BEARBEITUNGSMODUS_BEENDEN_DESCRIPTION',
        hideCancelButton: true,
        buttons: [
          {
            label: 'WEITER_BEARBEITEN',
            variant: 'primary',
            value: false,
          },
          {
            label: 'VERWERFEN',
            variant: 'secondary',
            value: true,
          },
        ],
      })
      .pipe(
        take(1),
        map((actionButton) => {
          if (typeof actionButton?.value === 'boolean') {
            // If user chose to discard changes, reset all forms
            if (actionButton?.value === true) {
              this.resetAllComponents();
            }
            return actionButton?.value;
          } else {
            throw new Error('showConfirmEditModeCancelModal: Button value is not a boolean');
          }
        }),
      );
  }

  public registerComponent(component: FormEditInlineModel): void {
    this.activeComponents.add(component);
  }

  public unregisterComponent(component: FormEditInlineModel): void {
    this.activeComponents.delete(component);
  }

  public async checkUnsavedChanges(): Promise<boolean> {
    if (!this.hasUnsavedChanges) {
      return true;
    }
    try {
      return await firstValueFrom(this.showConfirmEditModeCancelModal());
    } catch {
      return false;
    }
  }

  // For synchronous usage (like in guards)
  public checkUnsavedChangesSync(): Observable<boolean> | boolean {
    if (this.hasUnsavedChanges) {
      return this.showConfirmEditModeCancelModal();
    }
    return true;
  }

  private resetAllForms(): void {
    Array.from(this.activeComponents).forEach((component) => {
      if (typeof component.resetForm === 'function') {
        component.resetForm();
      }
    });
  }

  private resetAllEditModes(): void {
    Array.from(this.activeComponents).forEach((component) => {
      if (component.editMode !== undefined) {
        component.editMode = false;
      }
    });
  }

  private resetAllComponents(): void {
    this.resetAllForms();
    this.resetAllEditModes();
  }
}
